import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { isCatSoundOn, playHappyChirp, playSadWhimper } from '../utils/catMeow';
import './CatDecorations.css';

// SVG-декор вместо эмодзи (эмодзи рендерились по-разному в разных ОС
// и ломали цельность арта). Размер задаётся через 1em → font-size из CSS
// продолжает управлять масштабом, как раньше.
const Leaf = () => (
  <svg viewBox="0 0 40 44" width="1em" height="1em" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 2 C33 10 36 28 20 42 C4 28 7 10 20 2 Z" fill="#56a86a" />
    <path d="M20 6 L20 38" stroke="#2d6e42" strokeWidth="1.6" opacity="0.5" strokeLinecap="round" />
    <path d="M20 16 L12 12 M20 22 L28 18 M20 28 L13 25" stroke="#2d6e42" strokeWidth="1.1" opacity="0.4" strokeLinecap="round" />
  </svg>
);

const Paw = () => (
  <svg viewBox="0 0 32 30" width="1em" height="1em" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="16" cy="21" rx="8" ry="6.5" fill="#e89ab8" />
    <circle cx="6.5" cy="13" r="3" fill="#e89ab8" />
    <circle cx="12.5" cy="8.5" r="3.1" fill="#e89ab8" />
    <circle cx="19.5" cy="8.5" r="3.1" fill="#e89ab8" />
    <circle cx="25.5" cy="13" r="3" fill="#e89ab8" />
  </svg>
);

const SPARKLES = [
  { char: '✦', top: '8%',  left: '3%',  delay: '0s',    size: '16px' },
  { char: '✧', top: '14%', left: '96%', delay: '0.6s',  size: '14px' },
  { char: '✦', top: '32%', left: '2%',  delay: '1.2s',  size: '13px' },
  { char: '✧', top: '45%', left: '97%', delay: '0.35s', size: '15px' },
  { char: '✦', top: '68%', left: '4%',  delay: '0.9s',  size: '14px' },
  { char: '✧', top: '78%', left: '95%', delay: '1.5s',  size: '16px' },
  { char: '✦', top: '5%',  left: '48%', delay: '0.45s', size: '13px' },
  { char: '✧', top: '22%', left: '72%', delay: '1.1s',  size: '12px' },
];

// Текст стикеров зависит от того, что сейчас на экране.
// Каждая запись — [верхняя строка, нижняя строка] для двух стикеров.
const STICKER_TEXT = {
  error:    [['ой… тут что-то', 'не сходится 😿'], ['глянь строки', 'с ошибкой, мяу']],
  offer:    [['всё верно', 'посчитано! ♡'],       ['КП готово —', 'можно слать ✨']],
  counting: [['считаю', 'варианты… ✦'],           ['щас всё', 'разложу, мур']],
  empty:    [['добавь товар', 'и поехали ✨'],     ['жду данные,', 'ня~ ♡']],
};

// Реплика Мурки в бале рядом с ней — тоже зависит от состояния экрана.
const BUBBLE_TEXT = {
  error:    'ой, тут ошибка… поправим? 😿',
  offer:    'готово! КП собрала ♡',
  counting: 'считаю варианты, мур~',
  empty:    'привет! добавь товар ✨',
};

export default function CatDecorations({ status = 'empty' }) {
  const [sticky1, sticky2] = STICKER_TEXT[status] || STICKER_TEXT.empty;
  const bubble = BUBBLE_TEXT[status] || BUBBLE_TEXT.empty;

  // Одноразовая реакция Мурки на смену состояния: радость на готовое КП,
  // грусть на ошибку. Класс снимается через ~1с.
  const [reaction, setReaction] = useState(null);
  const prevStatus = useRef(status);
  useEffect(() => {
    if (status === prevStatus.current) return;
    prevStatus.current = status;
    if (status !== 'offer' && status !== 'error') return;
    setReaction(status === 'offer' ? 'happy' : 'sad');
    if (isCatSoundOn()) {
      if (status === 'offer') playHappyChirp();
      else playSadWhimper();
    }
    const t = setTimeout(() => setReaction(null), 1000);
    return () => clearTimeout(t);
  }, [status]);

  return ReactDOM.createPortal(
    <div className="cg-decor-root" aria-hidden="true">

      {/* ── Кластер листьев: верхний правый угол ── */}
      <div className="cg-corner cg-corner--tr">
        <span className="cg-corner__plant"><Leaf /></span>
        <span className="cg-corner__plant"><Leaf /></span>
        <span className="cg-corner__plant"><Leaf /></span>
        <span className="cg-corner__plant"><Leaf /></span>
        <span className="cg-corner__plant"><Leaf /></span>
        <span className="cg-corner__sparkle">✦</span>
        <span className="cg-corner__sparkle">✧</span>
      </div>

      {/* ── Кластер листьев: нижний левый угол ── */}
      <div className="cg-corner cg-corner--bl">
        <span className="cg-corner__plant"><Leaf /></span>
        <span className="cg-corner__plant"><Leaf /></span>
        <span className="cg-corner__plant"><Leaf /></span>
        <span className="cg-corner__plant"><Leaf /></span>
        <span className="cg-corner__plant"><Leaf /></span>
        <span className="cg-corner__sparkle">✦</span>
        <span className="cg-corner__sparkle">✧</span>
      </div>

      {/* ── Разбросанные искорки ── */}
      {SPARKLES.map((s, i) => (
        <span
          key={i}
          className="cg-sparkle"
          style={{
            top: s.top,
            left: s.left,
            fontSize: s.size,
            animationDelay: s.delay,
          }}
        >
          {s.char}
        </span>
      ))}

      {/* ── Стикеры (текст зависит от состояния экрана) ── */}
      <div className="cg-sticky cg-sticky--1">
        {sticky1[0]}<br />{sticky1[1]}
      </div>
      <div className="cg-sticky cg-sticky--2">
        {sticky2[0]}<br />{sticky2[1]}
      </div>

      {/* ── Следы лапок ── */}
      <span className="cg-paw cg-paw--1"><Paw /></span>
      <span className="cg-paw cg-paw--2"><Paw /></span>
      <span className="cg-paw cg-paw--3"><Paw /></span>

      {/* ── Мурка справа + её реплика ── */}
      <div key={status} className="cg-bubble">{bubble}</div>
      <img
        src="/cat-girl-right.png"
        className={`cg-catgirl cg-catgirl--right${reaction ? ` cg-catgirl--${reaction}` : ''}`}
        alt=""
        draggable="false"
      />
      <img
        src="/cat-girl-left.png"
        className="cg-catgirl cg-catgirl--left"
        alt=""
        draggable="false"
      />

      {/* ── Вьюны по бокам ── */}
      <div className="cg-vine cg-vine--left" aria-hidden="true" />
      <div className="cg-vine cg-vine--right" aria-hidden="true" />

    </div>,
    document.body
  );
}
